import { useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import { formatUSPhoneNumber } from 'common/utils'
import { useSendWalletCardMutation } from 'lib/services/api/patient-service/sendWalletCard'
import { useApolloClients } from 'lib/apollo/apolloProvider'
import {
  PatientWalletCardSenderProps,
  PatientWalletCardFormData,
  PatientWalletCardFormErrors,
} from './PatientWalletCardSender.types'

export const usePatientWalletCardSenderLogic = (
  props: PatientWalletCardSenderProps
) => {
  const {
    surgeryId,
    patientId,
    patientName = '',
    phoneNumber = '',
    dateOfBirth,
    surgeonName,
    hospitalName,
  } = props

  // Parse patientName into first and last name
  const parseName = (fullName: string) => {
    const nameParts = fullName.trim().split(' ')
    if (nameParts.length === 0) return { firstName: '', lastName: '' }
    if (nameParts.length === 1) return { firstName: nameParts[0], lastName: '' }
    const firstName = nameParts[0]
    const lastName = nameParts.slice(1).join(' ')
    return { firstName, lastName }
  }

  const { firstName, lastName } = parseName(patientName || '')

  const [formData, setFormData] = useState<PatientWalletCardFormData>({
    firstName,
    lastName,
    email: '',
    phoneNumber: phoneNumber || '',
  })

  const [errors, setErrors] = useState<PatientWalletCardFormErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const { iservicesClient } = useApolloClients()
  const { sendWalletCard } = useSendWalletCardMutation(iservicesClient)

  const validateForm = (): boolean => {
    const newErrors: PatientWalletCardFormErrors = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }

    const hasEmail = formData.email?.trim() || ''
    const hasPhone = formData.phoneNumber?.trim() || ''

    // At least one contact method is required
    if (!hasEmail && !hasPhone) {
      newErrors.email = 'Either email or phone number is required'
      newErrors.phoneNumber = 'Either email or phone number is required'
    } else {
      // Validate email format if provided
      if (hasEmail && !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address'
      }

      // If phone number is provided but incomplete, show error
      if (
        formData.phoneNumber &&
        formData.phoneNumber.length > 0 &&
        !formData.phoneNumber.match(/^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/)
      ) {
        newErrors.phoneNumber = 'Complete phone number required'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (
    name: keyof PatientWalletCardFormData,
    value: string
  ) => {
    if (name === 'phoneNumber') {
      const formattedPhone = formatUSPhoneNumber(value.replace(/\D/g, ''))
      setFormData((prev) => ({ ...prev, [name]: formattedPhone }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }))
    }
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      // Call the wallet card API
      const response = await sendWalletCard({
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email || undefined,
        phone: formData.phoneNumber || undefined,
        patientId,
        surgeryId,
        dateOfBirth,
        surgeonName,
        hospitalName,
      })

      // Handle the response
      if (response?.data?.createWalletInvitation) {
        const walletInvitation = response.data.createWalletInvitation

        // Check if the operation was successful
        if (walletInvitation.success && walletInvitation.data) {
          enqueueSnackbar('Wallet card invitation sent successfully!', {
            variant: 'success',
          })
          setIsSuccess(true)
        } else {
          // Handle failure case with the message from the API
          const errorMessage =
            walletInvitation.message ||
            'Failed to send wallet card invitation. Please try again.'

          enqueueSnackbar(errorMessage, {
            variant: 'error',
          })
        }
      } else {
        // Handle case where we got a partial or malformed response
        enqueueSnackbar(
          'Failed to send wallet card invitation. Please try again.',
          {
            variant: 'error',
          }
        )
      }
    } catch (error: any) {
      console.error('Wallet card error:', error)

      // Try to extract error message from Apollo error response
      let errorMessage =
        'Failed to send wallet card invitation. Please try again.'

      // Check for GraphQL errors in the response
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message || errorMessage
      }

      // Try to get message from network error
      if (
        error.networkError &&
        error.networkError.result &&
        error.networkError.result.errors
      ) {
        errorMessage =
          error.networkError.result.errors[0].message || errorMessage
      }

      enqueueSnackbar(errorMessage, {
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return {
    formData,
    errors,
    isLoading,
    isSuccess,
    handleInputChange,
    handleSubmit,
  }
}
